import React from 'react';
import moment from 'moment';
import { useTranslation, Trans } from 'react-i18next';
import {
    getVehiclePriceEstimateModalUrl,
    getVehicleVertical,
    getVerticalName,
    isVehicleOld,
    vehicleHasAd,
    isVehicleGoodForBaana,
    sellVehicleUrl,
    VERTICAL,
} from 'lib/utils/store/vehicle';
import { UTM_CAMPAIGN, UTM_CONTENT } from 'lib/utm';
import Button from 'components/UI/Button';
import Link from 'components/Link';
import { isProductionHost } from 'lib/env';
import { IVehicle } from 'components/Vehicles/VehiclePriceEstimate/VehiclePriceEstimateDisplayNew/types';
import { inflect } from 'lib/translate';
import { urlWithLangParam } from 'lib/helpers';

import './price-cloud-new.pcss';
import CreateAdSketchButton from '../../CreateAd/CreateAdSketchButton/CreateAdSketchButton';
import { getLocale } from '../../../../lib/utils/getLocale';
import { useVehicleOwner } from '../../../../lib/hooks/useVehicleOwner';

export function PriceCloudNew ({
    vehicle,
    onClose,
    withUpdateLink,
}: {
    vehicle: IVehicle,
    onClose?: () => void,
    withUpdateLink?: boolean,
}) {
    const { t, i18n } = useTranslation('vehicles');
    const { id, estimatedAt, adUrl } = vehicle;
    const vertical: string = getVehicleVertical(vehicle);
    const verticalPronoun: string = inflect(t, getVerticalName(t, getVehicleVertical(vehicle)), 'pronoun');
    const dateEstimatedAt = moment(estimatedAt).format('l');
    const env = isProductionHost() ? 'production' : 'development';
    const showBaanaLink = isVehicleGoodForBaana({ vehicle, vertical });
    const urlForSellVehicle = urlWithLangParam(sellVehicleUrl(vehicle), i18n.language);

    const vehicleId = vehicle?.id?.toString();
    const { error, data } = useVehicleOwner(vehicleId);
    const result = data?.getTrafiInspectionAndOwnerData;

    const isEligibleVehicleType = vertical === VERTICAL.CAR || vertical === VERTICAL.BIKE;
    const hasOwnerData = !error && result?.vehicle;
    const hasNoVerifiedAd = !result?.vehicle?.hasVerifiedSalesAd;
    const isSuitable = isEligibleVehicleType && hasOwnerData && hasNoVerifiedAd;
    const verifyAdPath = vehicle?.id ? `${getLocale(i18n.language)}/jata-ilmoitus/${vehicle.id}` : '';

    const shouldShowAdCreation = !isSuitable && !(isVehicleOld(vehicle) && !vehicleHasAd(vehicle));

    return (
        <div>
            {withUpdateLink && (
                <Trans
                    i18nKey="vehicles:pricePrediction.infoText.priceCloudWithUpdate"
                    {...{ vertical: verticalPronoun, date: dateEstimatedAt }}
                >
                    <Link
                        to={getVehiclePriceEstimateModalUrl(location, id)}
                        trackName="editPricePrediction"
                    >
                        {t('vehicles:pricePrediction.cta.updatePriceEstimate')}
                    </Link>
                    - price estimate done at {{ date: dateEstimatedAt }}. Estimate based on vehicles sold
                    at {{ vertical: verticalPronoun }}.
                </Trans>
            )}
            {!withUpdateLink && (
                <>
                    <div className="price-cloud-prediction-text">
                        {t('vehicles:pricePrediction.infoText.priceCloudWithUpdateNew', { vertical: verticalPronoun })}
                    </div>
                    {shouldShowAdCreation && (
                        <div className="">
                            {!adUrl && (
                                <div className="price-cloud-actions">
                                    <h2>
                                        {t(`vehicles:actions.createAdIn.${vertical}.linkText`)}
                                    </h2>
                                    <p>
                                        {t(`vehicles:actions.createAdIn.${vertical}.info`)}
                                    </p>
                                    <div>
                                        <Link
                                            className="button button--centered link--create-ad"
                                            to={urlForSellVehicle}
                                            trackName="createAd"
                                        >
                                            {t(`vehicles:actions.sellIn.${vertical}.linkTextNew`)}
                                        </Link>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {(isSuitable && verifyAdPath) && (
                        <div className="">
                            <div className="price-cloud-actions">
                                <h2>
                                    {t(`vehicles:actions.verifiedAd.${vertical}.linkText`)}
                                </h2>
                                <p>
                                    {t(`vehicles:actions.verifiedAd.${vertical}.info`)}
                                </p>
                                <div>
                                    <CreateAdSketchButton
                                        to={verifyAdPath}
                                        className="button button--centered link--create-ad"
                                        vehicle={vehicle}
                                        text={t(`vehicles:actions.verifiedAd.${vertical}.linkTextNew`)}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
            {showBaanaLink && (
                <div className="baana">
                    <h2>
                        {t('vehicles:actions.baana.title')}
                        <div className="baana-new">{t('vehicles:actions.baana.new')}</div>
                    </h2>
                    <p>
                        {t('vehicles:actions.baana.info')}
                    </p>
                    <div>
                        <Link
                            className="button button--centered baana-link"
                            to={t(`vehicles:actions.baana.url.${env}`)}
                            trackName="gotoBaana"
                            utmTag={{
                                campaign: UTM_CAMPAIGN.HINTAARVIO,
                                content: UTM_CONTENT.MYY
                            }}
                        >
                            {t('vehicles:actions.baana.button')}
                        </Link>
                    </div>
                </div>
            )}
            <div className="price-cloud-still-thinking">
                <Button
                    className="button button--secondary button--centered button--considering"
                    trackName="stillConsidering"
                    onClick={onClose}
                >
                    {adUrl ? t('vehicles:actions.sellIn.close') : t('vehicles:actions.sellIn.stillConsidering')}
                </Button>
            </div>
        </div>
    );
}
